import React, { useState, useEffect } from "react";
import { Modal, ProgressBar, Button } from "react-bootstrap";
import type { Resource, Tag, ResourceType } from "./types";
import TagMultiSelect from "../TagMultiSelect";
import ApiService from "../../_services/ApiService";
import StudyResourceService from "../../_services/StudyResourceService";
import ResourcePoolBrowser from "../ResourcePoolBrowser";
import QuizFormPanel from "../../pages/quick-quiz-maker/QuizFormPanel";
import SpinWheelEditor from "../../pages/study-resources/components/SpinWheelEditor";
import useGoogleDrivePicker from "../../hooks/useGoogleDrivePicker";

type ResourceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  resources: Resource[];
  sectionName: string;
  chapterName: string;
  chapterIndex: number;
  sectionIndex: number;
  onAddResource?: () => void;
  onRemoveResource?: (resourceIndex: number) => void;
  onUpdateResource?: (chapterIndex: number, sectionIndex: number, resourceIndex: number, updates: Partial<Resource>) => void;
  availableTags?: Tag[];
  resourceTypes: ResourceType[];
  isViewMode: boolean;
};

export default function ResourceModal({
  isOpen,
  onClose,
  resources,
  sectionName,
  chapterName,
  chapterIndex,
  sectionIndex,
  onAddResource,
  onRemoveResource,
  onUpdateResource,
  availableTags = [],
  resourceTypes,
  isViewMode,
}: ResourceModalProps) {
  const [expandedResource, setExpandedResource] = useState<number | null>(null);
  // Local state to hold temporary edits before saving
  const [editedResources, setEditedResources] = useState<Resource[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  // State for Resource Pool Browser
  const [showResourcePool, setShowResourcePool] = useState(false);
  // State for quiz creation modal
  const [quizModalIndex, setQuizModalIndex] = useState<number | null>(null);
  // State for unsaved-changes confirmation modal
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  // State for file upload (E-Book / Download types)
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadTab, setUploadTab] = useState<"url" | "upload" | "gdrive">("url");
  const [gdriveUploading, setGdriveUploading] = useState(false);
  const { openPicker: openGDrivePicker, loading: gdrivePickerLoading } = useGoogleDrivePicker();

  // Initialize editedResources when modal opens or resources change
  React.useEffect(() => {
    if (isOpen) {
      setEditedResources(JSON.parse(JSON.stringify(resources))); // Deep clone
      setHasChanges(false);
    }
  }, [isOpen, resources]);

  // Reset upload state when switching between resources
  useEffect(() => {
    setSelectedFile(null);
    setUploadTab("url");
    setUploadProgress(0);
  }, [expandedResource]);

  if (!isOpen) return null;

  const toggleResourceExpand = (index: number) => {
    setExpandedResource(expandedResource === index ? null : index);
  };

  const updateLocalResource = (index: number, updates: Partial<Resource>) => {
    const updated = [...editedResources];
    updated[index] = { ...updated[index], ...updates };
    setEditedResources(updated);
    setHasChanges(true);
  };

  const handleSave = async () => {
    console.log('[ResourceModal] Saving changes...');

    try {
      // Apply all changes to the actual module AND update tags in database
      for (let index = 0; index < editedResources.length; index++) {
        const resource = editedResources[index];
        const original = resources[index];

        if (onUpdateResource) {
          // Find what changed by comparing with original
          const changes: Partial<Resource> = {};

          (Object.keys(resource) as Array<keyof Resource>).forEach((key) => {
            if (JSON.stringify(resource[key]) !== JSON.stringify(original?.[key])) {
              (changes as any)[key] = resource[key];
            }
          });

          console.log('[ResourceModal] Resource', index, 'changes:', changes);

          // Only save if there are actual changes
          if (Object.keys(changes).length > 0) {
            console.log('[ResourceModal] Calling onUpdateResource for resource', index);
            // Check if this is a new resource by checking if original has an id
            const isNewResource = !original?.id;
            // For new resources, send the full resource; for existing, send only changes
            onUpdateResource(chapterIndex, sectionIndex, index, isNewResource ? resource : changes);

            // If tags changed and resource has an ID, update tags in database immediately
            if (changes.tags && resource.id) {
              const resourceId = Number(resource.id);
              const tagIds = resource.tags?.map((tag: Tag) => Number(tag.id)) || [];

              console.log(`[ResourceModal] Updating tags in database for resource ${resourceId}:`, tagIds);

              try {
                await ApiService.updateResourceTags(resourceId, tagIds);
                console.log(`[ResourceModal] ✓ Successfully updated tags for resource ${resourceId} in database`);
              } catch (error) {
                console.error(`[ResourceModal] Failed to update tags for resource ${resourceId}:`, error);
                alert(`Failed to update tags for resource ${resourceId} in database. Please try again.`);
                return; // Don't close the modal if tag update failed
              }
            }
          } else {
            console.log('[ResourceModal] No changes detected for resource', index);
          }
        }
      }

      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('[ResourceModal] Error saving changes:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  const handleCancel = () => {
    // Don't trigger cancel when a nested modal (quiz creation or resource pool) is open
    if (quizModalIndex !== null || showResourcePool) return;
    if (hasChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  // Handler for adding a new blank resource
  const handleAddNewResource = () => {
    const newResource: Resource = {
      name: "New Resource",
      resourceType: "studyResource",
      url: "",
      sortOrder: editedResources.length + 1,
    };
    const updated = [...editedResources, newResource];
    setEditedResources(updated);
    setHasChanges(true);
    // Expand the newly added resource
    setExpandedResource(updated.length - 1);
  };

  // Handler for adding a resource from the pool
  const handleSelectResourceFromPool = (poolResource: any) => {
    const newResource: Resource = {
      name: poolResource.name,
      resourceType: "studyResource",
      studyResourceTypeId: poolResource.typeId,
      studyResourceSubTypeId: poolResource.subTypeId || undefined,
      url: poolResource.url || undefined,
      content: poolResource.content || undefined,
      sortOrder: editedResources.length + 1,
      id: poolResource.id,
    };

    // If the last resource is a blank placeholder (from "+Add Resource"), replace it
    const last = editedResources[editedResources.length - 1];
    const isBlankPlaceholder = last && last.name === "New Resource" && !last.url && !last.content && !last.id;

    let updated: Resource[];
    if (isBlankPlaceholder) {
      updated = [...editedResources.slice(0, -1), { ...newResource, sortOrder: editedResources.length }];
    } else {
      updated = [...editedResources, newResource];
    }
    setEditedResources(updated);
    setHasChanges(true);
  };

  // File upload handlers for E-Book (13) / Download (1) types
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      alert("File size must be less than 100MB");
      return;
    }
    setSelectedFile(file);
  };

  const handleFileUpload = async (index: number) => {
    if (!selectedFile) return;
    setUploadingIndex(index);
    setUploadProgress(0);
    try {
      const response = await StudyResourceService.uploadFileToS3(
        selectedFile,
        (progress) => setUploadProgress(progress)
      );
      updateLocalResource(index, { url: response.data.url });
      setSelectedFile(null);
      setUploadTab("url");
    } catch (err: any) {
      console.error("Upload failed:", err);
    } finally {
      setUploadingIndex(null);
      setUploadProgress(0);
    }
  };

  return (
    <div className="lmb-modal-overlay" onClick={handleCancel}>
      <div
        className="lmb-modal lmb-modal-large"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column' as const,
          overflow: 'hidden'
        }}
      >
        <div className="lmb-modal-header">
          <div>
            <h3>Resources</h3>
            <p className="lmb-muted" style={{ margin: "4px 0 0 0", fontSize: 13 }}>
              {chapterName} → {sectionName}
            </p>
          </div>
          <button className="lmb-modal-close" onClick={handleCancel} type="button">
            ×
          </button>
        </div>

        <div className="lmb-modal-body" style={{ flex: 1, overflowY: 'auto' as const, paddingBottom: 120 }}>
          {editedResources.length === 0 ? (
            <div className="lmb-empty">
              <p>No resources in this section yet.</p>
              {!isViewMode && (
                <p className="lmb-muted" style={{ marginTop: 8, fontSize: 13 }}>
                  Switch to the Builder tab to add resources.
                </p>
              )}
            </div>
          ) : (
            <div className="lmb-resource-list">
              {editedResources.map((resource, index) => {
                const isExpanded = expandedResource === index;
                return (
                  <div key={index} className="lmb-resource-modal-item">
                    <div className="lmb-resource-modal-header">
                      <div className="lmb-resource-modal-number">{index + 1}</div>
                      <div className="lmb-resource-modal-content">
                        {/* Resource Name - Editable */}
                        {!isViewMode ? (
                          <input
                            className="lmb-input lmb-resource-modal-name-input"
                            value={resource.name || ""}
                            onChange={(e) =>
                              updateLocalResource(index, { name: e.target.value })
                            }
                            placeholder="Enter resource name..."
                          />
                        ) : (
                          <div className="lmb-resource-modal-name">{resource.name || "Untitled Resource"}</div>
                        )}

                        {/* Collapsed View - Basic Info */}
                        {!isExpanded && (() => {
                          if (resource.resourceType === "test") {
                            return (
                              <div className="lmb-resource-modal-meta">
                                <span className="lmb-resource-modal-type">
                                  📝 Test
                                </span>
                                {resource.testId && (
                                  <span className="lmb-resource-modal-file">Test ID: {resource.testId}</span>
                                )}
                                {resource.testName && (
                                  <span className="lmb-resource-modal-file">{resource.testName}</span>
                                )}
                              </div>
                            );
                          } else {
                            const resourceType = resourceTypes.find(rt => rt.id === resource.studyResourceTypeId);
                            const subType = resourceType?.subtypes?.find(st => st.id === resource.studyResourceSubTypeId);
                            return (
                              <div className="lmb-resource-modal-meta">
                                <span className="lmb-resource-modal-type">
                                  {resourceType?.name || `Type ID: ${resource.studyResourceTypeId}`}
                                  {subType && ` / ${subType.name}`}
                                </span>
                                {resource.url && (
                                  <span className="lmb-resource-modal-url">
                                    <a href={resource.url} target="_blank" rel="noopener noreferrer">
                                      🔗 View Resource
                                    </a>
                                  </span>
                                )}
                                {resource.fileUrl && (
                                  <span className="lmb-resource-modal-file">📎 {resource.fileUrl}</span>
                                )}
                              </div>
                            );
                          }
                        })()}

                        {/* Tags Display/Edit */}
                        {!isExpanded && resource.tags && resource.tags.length > 0 && (
                          <div className="lmb-resource-modal-tags">
                            {resource.tags.map((tag) => (
                              <span key={tag.id} className="lmb-resource-tag">
                                {tag.displayLabel || tag.name}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Expanded View - Full Edit Form */}
                        {isExpanded && (() => {
                          const currentType = resourceTypes.find(rt => rt.id === resource.studyResourceTypeId);
                          const availableSubtypes = currentType?.subtypes || [];

                          return (
                            <div className="lmb-resource-modal-expanded">
                              {/* Resource Type Toggle (Study Resource / Test) */}
                              <div className="lmb-resource-modal-field">
                                <label className="lmb-muted lmb-label">Type</label>
                                {isViewMode ? (
                                  <div className="lmb-input lmb-input-sm" style={{ background: 'var(--surface-page)' }}>
                                    {resource.resourceType === "test" ? "Test" : "Study Resource"}
                                  </div>
                                ) : (
                                  <select
                                    className="lmb-input lmb-input-sm"
                                    value={resource.resourceType}
                                    onChange={(e) => {
                                      const newResourceType = e.target.value as "studyResource" | "test";
                                      updateLocalResource(index, {
                                        resourceType: newResourceType,
                                        // Clear type-specific fields when switching
                                        ...(newResourceType === "test" ? {
                                          studyResourceTypeId: undefined,
                                          studyResourceSubTypeId: undefined,
                                          content: undefined,
                                          fileUrl: undefined,
                                          url: undefined,
                                        } : {
                                          testId: undefined,
                                          testName: undefined,
                                        }),
                                      });
                                    }}
                                  >
                                    <option value="studyResource">Study Resource</option>
                                    <option value="test">Test</option>
                                  </select>
                                )}
                              </div>

                              {/* Study Resource Fields */}
                              {resource.resourceType === "studyResource" && (
                                <>
                                  <div className="lmb-resource-modal-field">
                                    <label className="lmb-muted lmb-label">Resource Type</label>
                                {isViewMode ? (
                                  <div className="lmb-input lmb-input-sm" style={{ background: 'var(--surface-page)' }}>
                                    {currentType?.name || `Unknown (ID: ${resource.studyResourceTypeId})`}
                                  </div>
                                ) : (
                                  <select
                                    className="lmb-input lmb-input-sm"
                                    value={resource.studyResourceTypeId || ""}
                                    onChange={(e) => {
                                      const newTypeId = Number(e.target.value);
                                      const newType = resourceTypes.find(t => t.id === newTypeId);
                                      const subtypes = newType?.subtypes || [];
                                      // Auto-select subtype if there's only one, otherwise clear it
                                      const autoSubTypeId = subtypes.length === 1 ? subtypes[0].id : undefined;
                                      updateLocalResource(index, {
                                        studyResourceTypeId: newTypeId,
                                        studyResourceSubTypeId: autoSubTypeId,
                                      });
                                    }}
                                  >
                                    <option value="">Select type...</option>
                                    {resourceTypes.map((type) => (
                                      <option key={type.id} value={type.id}>
                                        {type.name}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>

                              {availableSubtypes.length > 0 && (
                                <div className="lmb-resource-modal-field">
                                  <label className="lmb-muted lmb-label">
                                    Resource Sub-Type {availableSubtypes.length > 1 ? '(Optional)' : ''}
                                  </label>
                                  {isViewMode ? (
                                    <div className="lmb-input lmb-input-sm" style={{ background: 'var(--surface-page)' }}>
                                      {availableSubtypes.find(st => st.id === resource.studyResourceSubTypeId)?.name ||
                                        (resource.studyResourceSubTypeId ? `Unknown (ID: ${resource.studyResourceSubTypeId})` : 'None')}
                                    </div>
                                  ) : (
                                    <select
                                      className="lmb-input lmb-input-sm"
                                      value={resource.studyResourceSubTypeId || ""}
                                      onChange={(e) =>
                                        updateLocalResource(index, {
                                          studyResourceSubTypeId: e.target.value ? Number(e.target.value) : undefined,
                                        })
                                      }
                                    >
                                      <option value="">Select sub-type...</option>
                                      {availableSubtypes.map((subtype) => (
                                        <option key={subtype.id} value={subtype.id}>
                                          {subtype.name}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                              )}

                                  {/* Spin Wheel Game config (typeId 41) */}
                                  {resource.studyResourceTypeId === 41 ? (
                                    isViewMode ? (
                                      <div style={{ fontSize: 12, color: 'var(--text-body)' }}>
                                        {(() => { try { const c = JSON.parse(resource.content || '{}'); return `${(c.wedges || []).length} wedges`; } catch { return '—'; } })()}
                                      </div>
                                    ) : (
                                      <SpinWheelEditor
                                        content={resource.content}
                                        onChange={(json) => updateLocalResource(index, { content: json })}
                                      />
                                    )
                                  ) : resource.studyResourceTypeId === 40 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                      {/* Feature flags */}
                                      <div className="lmb-resource-modal-field">
                                        <label className="lmb-muted lmb-label">Options</label>
                                        {isViewMode ? (
                                          <div style={{ fontSize: 12, color: 'var(--text-body)' }}>
                                            {(() => { try { const c = JSON.parse(resource.content || '{}'); const parts: string[] = []; if (c.showJustification) parts.push('Justification required'); if (c.enableAIScoring) parts.push('AI scoring enabled'); return parts.length ? parts.join(', ') : 'Basic decision only'; } catch { return '—'; } })()}
                                          </div>
                                        ) : (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                              <input type="checkbox" checked={(() => { try { return !!JSON.parse(resource.content || '{}').showJustification; } catch { return false; } })()} onChange={(e) => {
                                                try {
                                                  const existing = JSON.parse(resource.content || '{}');
                                                  existing.showJustification = e.target.checked;
                                                  if (!e.target.checked) existing.enableAIScoring = false;
                                                  updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                                } catch { /* ignore */ }
                                              }} />
                                              Require student justification
                                            </label>
                                            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', opacity: (() => { try { return JSON.parse(resource.content || '{}').showJustification ? 1 : 0.5; } catch { return 0.5; } })() }}>
                                              <input type="checkbox" checked={(() => { try { return !!JSON.parse(resource.content || '{}').enableAIScoring; } catch { return false; } })()} disabled={(() => { try { return !JSON.parse(resource.content || '{}').showJustification; } catch { return true; } })()} onChange={(e) => {
                                                try {
                                                  const existing = JSON.parse(resource.content || '{}');
                                                  existing.enableAIScoring = e.target.checked;
                                                  updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                                } catch { /* ignore */ }
                                              }} />
                                              Enable AI scoring of justification
                                            </label>
                                          </div>
                                        )}
                                      </div>

                                      {/* Playbook title */}
                                      <div className="lmb-resource-modal-field">
                                        <label className="lmb-muted lmb-label">Playbook Title</label>
                                        <input
                                          className="lmb-input lmb-input-sm"
                                          value={(() => { try { return JSON.parse(resource.content || '{}').title || ''; } catch { return ''; } })()}
                                          onChange={(e) => {
                                            try {
                                              const existing = JSON.parse(resource.content || '{}');
                                              existing.title = e.target.value;
                                              updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                            } catch {
                                              updateLocalResource(index, { content: JSON.stringify({ title: e.target.value }, null, 2) });
                                            }
                                          }}
                                          placeholder="e.g. Payer Playbook — Cost vs Outcome Optimization"
                                          disabled={isViewMode}
                                        />
                                      </div>

                                      {/* Description */}
                                      <div className="lmb-resource-modal-field">
                                        <label className="lmb-muted lmb-label">Description</label>
                                        <input
                                          className="lmb-input lmb-input-sm"
                                          value={(() => { try { return JSON.parse(resource.content || '{}').description || ''; } catch { return ''; } })()}
                                          onChange={(e) => {
                                            try {
                                              const existing = JSON.parse(resource.content || '{}');
                                              existing.description = e.target.value;
                                              updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                            } catch {
                                              updateLocalResource(index, { content: JSON.stringify({ description: e.target.value }, null, 2) });
                                            }
                                          }}
                                          placeholder="Evaluate pathways and make a decision based on tradeoffs."
                                          disabled={isViewMode}
                                        />
                                      </div>

                                      {/* Workflow steps — v2 state machines are edited only in the full
                                          Workflow Builder; the CSV input here must never clobber one */}
                                      {(() => {
                                        let wf: any = undefined;
                                        try { wf = JSON.parse(resource.content || '{}').workflow; } catch { /* not JSON */ }
                                        const isMachine = !!wf && typeof wf === 'object' && !Array.isArray(wf) && wf.version === 2;
                                        if (isMachine) {
                                          return (
                                            <div className="lmb-resource-modal-field">
                                              <label className="lmb-muted lmb-label">Workflow</label>
                                              <div style={{ fontSize: 12, color: 'var(--text-muted)', border: '1px dashed var(--border-default)', borderRadius: 6, padding: '8px 10px' }}>
                                                ⚙ Executable state machine ({(wf.states || []).length} states, {(wf.transitions || []).length} transitions).
                                                Edit it in <strong>Study Resources → Edit → Workflow</strong>.
                                              </div>
                                            </div>
                                          );
                                        }
                                        return (
                                          <div className="lmb-resource-modal-field">
                                            <label className="lmb-muted lmb-label">Workflow Steps <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-faint)' }}>(comma-separated)</span></label>
                                            <input
                                              className="lmb-input lmb-input-sm"
                                              value={Array.isArray(wf) ? wf.join(', ') : ''}
                                              onChange={(e) => {
                                                try {
                                                  const existing = JSON.parse(resource.content || '{}');
                                                  existing.workflow = e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean);
                                                  updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                                } catch {
                                                  updateLocalResource(index, { content: JSON.stringify({ workflow: e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean) }, null, 2) });
                                                }
                                              }}
                                              placeholder="Patient, Diagnosis, Treatment, Utilization, Outcome, Cost"
                                              disabled={isViewMode}
                                            />
                                          </div>
                                        );
                                      })()}

                                      {/* Attributes Editor */}
                                      <div className="lmb-resource-modal-field">
                                        <label className="lmb-muted lmb-label">Attributes <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-faint)' }}>— define the metrics each pathway will have</span></label>
                                        {(() => {
                                          let attrs: any[] = [];
                                          try { attrs = JSON.parse(resource.content || '{}').attributes || []; } catch { /* empty */ }
                                          const updateAttribute = (ai: number, field: string, value: string) => {
                                            try {
                                              const existing = JSON.parse(resource.content || '{}');
                                              const a = existing.attributes || [];
                                              if (a[ai]) {
                                                const oldName = a[ai].name;
                                                a[ai][field] = value;
                                                // If name changed, rename key in all pathway values
                                                if (field === 'name' && oldName !== value && existing.pathways) {
                                                  existing.pathways.forEach((p: any) => {
                                                    if (p.values && oldName in p.values) {
                                                      p.values[value] = p.values[oldName];
                                                      delete p.values[oldName];
                                                    }
                                                  });
                                                }
                                                existing.attributes = a;
                                                updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                              }
                                            } catch { /* ignore */ }
                                          };
                                          const addAttribute = () => {
                                            try {
                                              const existing = JSON.parse(resource.content || '{}');
                                              const a = existing.attributes || [];
                                              a.push({ name: '', unit: '', direction: 'higher-is-better' });
                                              existing.attributes = a;
                                              updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                            } catch { /* ignore */ }
                                          };
                                          const removeAttribute = (ai: number) => {
                                            try {
                                              const existing = JSON.parse(resource.content || '{}');
                                              const removed = (existing.attributes || [])[ai];
                                              existing.attributes = (existing.attributes || []).filter((_: any, i: number) => i !== ai);
                                              // Remove from pathway values
                                              if (removed && existing.pathways) {
                                                existing.pathways.forEach((p: any) => { if (p.values) delete p.values[removed.name]; });
                                              }
                                              updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                            } catch { /* ignore */ }
                                          };
                                          return (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                              {attrs.map((attr: any, ai: number) => (
                                                <div key={ai} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                  <input className="lmb-input lmb-input-sm" value={attr.name || ''} onChange={(e) => updateAttribute(ai, 'name', e.target.value)} placeholder="Attribute name" disabled={isViewMode} style={{ fontSize: 12, flex: 1 }} />
                                                  <input className="lmb-input lmb-input-sm" value={attr.unit || ''} onChange={(e) => updateAttribute(ai, 'unit', e.target.value)} placeholder="Unit" disabled={isViewMode} style={{ fontSize: 12, maxWidth: 70 }} />
                                                  <select className="lmb-input lmb-input-sm" value={attr.direction || 'higher-is-better'} onChange={(e) => updateAttribute(ai, 'direction', e.target.value)} disabled={isViewMode} style={{ fontSize: 11, maxWidth: 140 }}>
                                                    <option value="higher-is-better">Higher is better</option>
                                                    <option value="lower-is-better">Lower is better</option>
                                                  </select>
                                                  {!isViewMode && attrs.length > 1 && (
                                                    <button type="button" onClick={() => removeAttribute(ai)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }} title="Remove">×</button>
                                                  )}
                                                </div>
                                              ))}
                                              {!isViewMode && (
                                                <button type="button" onClick={addAttribute} style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, background: 'var(--surface-sunken)', color: 'var(--text-body)', border: '1px dashed var(--border-strong)', borderRadius: 6, cursor: 'pointer', alignSelf: 'flex-start' }}>
                                                  + Add Attribute
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>

                                      {/* Visual Pathway Editor */}
                                      <div className="lmb-resource-modal-field">
                                        <label className="lmb-muted lmb-label">Pathways</label>
                                        {(() => {
                                          let pathways: any[] = [];
                                          let attrs: any[] = [];
                                          try {
                                            const parsed = JSON.parse(resource.content || '{}');
                                            pathways = parsed.pathways || [];
                                            attrs = parsed.attributes || [];
                                          } catch { /* empty */ }
                                          const borderColors = ['#e53e3e', '#38a169', '#d69e2e', '#805ad5'];
                                          const updatePathwayField = (pi: number, field: string, value: any) => {
                                            try {
                                              const existing = JSON.parse(resource.content || '{}');
                                              const pw = existing.pathways || [];
                                              if (pw[pi]) {
                                                pw[pi][field] = value;
                                                existing.pathways = pw;
                                                updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                              }
                                            } catch { /* ignore */ }
                                          };
                                          const updatePathwayValue = (pi: number, attrName: string, value: string) => {
                                            try {
                                              const existing = JSON.parse(resource.content || '{}');
                                              const pw = existing.pathways || [];
                                              if (pw[pi]) {
                                                if (!pw[pi].values) pw[pi].values = {};
                                                pw[pi].values[attrName] = value;
                                                existing.pathways = pw;
                                                updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                              }
                                            } catch { /* ignore */ }
                                          };
                                          const addPathway = () => {
                                            try {
                                              const existing = JSON.parse(resource.content || '{}');
                                              const pw = existing.pathways || [];
                                              const ids = 'ABCDEFGH';
                                              const nextId = ids[pw.length] || String(pw.length + 1);
                                              pw.push({ id: nextId, label: `Pathway ${nextId}`, title: '', steps: [], description: '', values: {} });
                                              existing.pathways = pw;
                                              updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                            } catch { /* ignore */ }
                                          };
                                          const removePathway = (pi: number) => {
                                            try {
                                              const existing = JSON.parse(resource.content || '{}');
                                              existing.pathways = (existing.pathways || []).filter((_: any, i: number) => i !== pi);
                                              updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                            } catch { /* ignore */ }
                                          };
                                          return (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                              {pathways.map((p: any, pi: number) => (
                                                <div key={pi} style={{ border: '1px solid var(--border-default)', borderLeft: `4px solid ${borderColors[pi] || '#6b7280'}`, borderRadius: '8px', padding: '12px 14px', background: 'var(--surface-page)' }}>
                                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                    <span style={{ fontSize: 13, fontWeight: 700, color: borderColors[pi] || '#6b7280' }}>{p.label || `Pathway ${pi + 1}`}</span>
                                                    {!isViewMode && pathways.length > 2 && (
                                                      <button type="button" onClick={() => removePathway(pi)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }} title="Remove pathway">×</button>
                                                    )}
                                                  </div>
                                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                    <div>
                                                      <label style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' as const }}>Label</label>
                                                      <input className="lmb-input lmb-input-sm" value={p.label || ''} onChange={(e) => updatePathwayField(pi, 'label', e.target.value)} disabled={isViewMode} style={{ fontSize: 12 }} />
                                                    </div>
                                                    <div>
                                                      <label style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' as const }}>Title</label>
                                                      <input className="lmb-input lmb-input-sm" value={p.title || ''} onChange={(e) => updatePathwayField(pi, 'title', e.target.value)} placeholder="e.g. Conservative Management" disabled={isViewMode} style={{ fontSize: 12 }} />
                                                    </div>
                                                    {attrs.map((attr: any, ai: number) => (
                                                      <div key={ai}>
                                                        <label style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' as const }}>{attr.name || `Attr ${ai + 1}`}{attr.unit ? ` (${attr.unit})` : ''}</label>
                                                        <input className="lmb-input lmb-input-sm" value={(p.values || {})[attr.name] || ''} onChange={(e) => updatePathwayValue(pi, attr.name, e.target.value)} disabled={isViewMode} style={{ fontSize: 12 }} />
                                                      </div>
                                                    ))}
                                                  </div>
                                                  <div style={{ marginTop: 8 }}>
                                                    <label style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' as const }}>Steps <span style={{ fontWeight: 400, textTransform: 'none' as const }}>(comma-separated)</span></label>
                                                    <input className="lmb-input lmb-input-sm" value={Array.isArray(p.steps) ? p.steps.join(', ') : ''} onChange={(e) => updatePathwayField(pi, 'steps', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))} placeholder="Primary Care, Generic Rx, Telehealth Follow-up" disabled={isViewMode} style={{ fontSize: 12 }} />
                                                  </div>
                                                  <div style={{ marginTop: 8 }}>
                                                    <label style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' as const }}>Description</label>
                                                    <textarea className="lmb-textarea" value={p.description || ''} onChange={(e) => updatePathwayField(pi, 'description', e.target.value)} rows={2} disabled={isViewMode} style={{ fontSize: 12 }} />
                                                  </div>
                                                </div>
                                              ))}
                                              {!isViewMode && pathways.length < 4 && (
                                                <button type="button" onClick={addPathway} style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600, background: 'var(--surface-sunken)', color: 'var(--text-body)', border: '1px dashed var(--border-strong)', borderRadius: 8, cursor: 'pointer' }}>
                                                  + Add Pathway
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>

                                      {/* Evaluation Criteria (used when AI scoring is enabled) */}
                                      <div className="lmb-resource-modal-field">
                                        <label className="lmb-muted lmb-label">
                                          AI Evaluation Criteria
                                          <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-faint)', marginLeft: 8 }}>
                                            One criterion per line — AI uses these to score student justifications
                                          </span>
                                        </label>
                                        <textarea
                                          className="lmb-textarea"
                                          value={(() => {
                                            try {
                                              const parsed = JSON.parse(resource.content || '{}');
                                              return (parsed.evaluationCriteria || []).join('\n');
                                            } catch { return ''; }
                                          })()}
                                          onChange={(e) => {
                                            try {
                                              const existing = JSON.parse(resource.content || '{}');
                                              existing.evaluationCriteria = e.target.value.split('\n').map((s: string) => s.trim()).filter(Boolean);
                                              updateLocalResource(index, { content: JSON.stringify(existing, null, 2) });
                                            } catch {
                                              // ignore parse errors while typing
                                            }
                                          }}
                                          placeholder={"Considers cost-effectiveness and budget implications\nAddresses clinical outcome quality and patient impact\nAcknowledges risk tradeoffs (readmission, complications)\nReferences specific data from the pathway options\nDemonstrates systems-level thinking"}
                                          rows={4}
                                          disabled={isViewMode}
                                          style={{ fontSize: '12px' }}
                                        />
                                      </div>

                                      {/* Sample data loader */}
                                      {!isViewMode && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                          {[
                                            { category: 'Healthcare & Life Sciences', samples: [
                                              { label: 'Payer Playbook', data: { showJustification: false, enableAIScoring: false, title: 'Payer Playbook — Cost vs Outcome Optimization', description: 'Evaluate the treatment pathways below and make a decision based on cost and outcome tradeoffs.', workflow: ['Patient', 'Diagnosis', 'Treatment', 'Utilization', 'Outcome', 'Cost'], attributes: [{ name: 'Cost', unit: '$', direction: 'lower-is-better' }, { name: 'Outcome', unit: '', direction: 'higher-is-better' }, { name: 'Readmission Risk', unit: '%', direction: 'lower-is-better' }], pathways: [{ id: 'A', label: 'Pathway A', title: 'Conservative Management', steps: ['Primary Care', 'Generic Rx', 'Telehealth Follow-up'], description: 'Lower upfront cost using generic medications and telemedicine.', values: { 'Cost': '12000', 'Outcome': 'Medium', 'Readmission Risk': '18' } }, { id: 'B', label: 'Pathway B', title: 'Balanced Protocol', steps: ['Specialist Consult', 'Targeted Therapy', 'In-Person Monitoring'], description: 'Balanced approach with specialist involvement and targeted treatment.', values: { 'Cost': '28000', 'Outcome': 'High', 'Readmission Risk': '8' } }, { id: 'C', label: 'Pathway C', title: 'Aggressive Intervention', steps: ['Multi-Specialist Team', 'Advanced Biologics', 'Continuous Monitoring', 'Rehab Program'], description: 'Highest cost but best clinical outcomes.', values: { 'Cost': '65000', 'Outcome': 'Best', 'Readmission Risk': '3' } }] } },
                                              { label: 'Provider Playbook', data: { showJustification: false, enableAIScoring: false, title: 'Provider Playbook — Care Pathway Optimization', description: 'Optimize the care pathway balancing length of stay against readmission risk.', workflow: ['Patient', 'Diagnosis', 'Treatment', 'Length of Stay', 'Outcome'], attributes: [{ name: 'Cost', unit: '$', direction: 'lower-is-better' }, { name: 'Outcome', unit: '', direction: 'higher-is-better' }, { name: 'Readmission Risk', unit: '%', direction: 'lower-is-better' }], pathways: [{ id: 'A', label: 'Pathway A', title: 'Early Discharge Protocol', steps: ['Rapid Assessment', 'Accelerated Treatment', 'Early Discharge', 'Home Monitoring'], description: 'Shorter length of stay, higher readmission risk.', values: { 'Cost': '8500', 'Outcome': 'Moderate', 'Readmission Risk': '22' } }, { id: 'B', label: 'Pathway B', title: 'Standard Care Protocol', steps: ['Full Workup', 'Phased Treatment', 'Step-Down Care', 'Discharge Planning'], description: 'Longer LOS with structured step-down care.', values: { 'Cost': '18000', 'Outcome': 'High', 'Readmission Risk': '6' } }] } },
                                              { label: 'Life Sciences', data: { showJustification: true, enableAIScoring: true, title: 'Life Sciences Playbook — Treatment Strategy Optimization', description: 'Choose a therapy strategy balancing response rate against scalability.', workflow: ['Patient', 'Therapy', 'Response', 'Progression', 'Outcome'], attributes: [{ name: 'Cost', unit: '$', direction: 'lower-is-better' }, { name: 'Response Rate', unit: '%', direction: 'higher-is-better' }, { name: 'Scalability', unit: '', direction: 'higher-is-better' }], pathways: [{ id: 'A', label: 'Pathway A', title: 'Precision Therapy', steps: ['Genomic Profiling', 'Biomarker Matching', 'Targeted Agent', 'Adaptive Monitoring'], description: 'Personalized treatment based on genomic profiling.', values: { 'Cost': '85000', 'Response Rate': '78', 'Scalability': 'Low' } }, { id: 'B', label: 'Pathway B', title: 'Standard Protocol', steps: ['Standard Screening', 'First-Line Therapy', 'Periodic Review'], description: 'Standardized treatment protocol applicable at scale.', values: { 'Cost': '22000', 'Response Rate': '45', 'Scalability': 'High' } }], evaluationCriteria: ['Considers cost-effectiveness and budget implications', 'Addresses response rate differences between options', 'Acknowledges scalability tradeoffs', 'References specific data from the pathway options', 'Demonstrates systems-level thinking'] } },
                                            ] },
                                            { category: 'Finance', samples: [
                                              { label: 'HSA/FSA Member Playbook', data: { showJustification: true, enableAIScoring: true, title: 'HSA/FSA Member Playbook — Contribution Strategy Optimization', description: 'Help a member choose an HSA/FSA contribution strategy balancing tax savings, take-home cash flow, and healthcare affordability.', workflow: ['Member Inquiry', 'Financial Assessment', 'Contribution Strategy', 'Benefit Utilization', 'Healthcare Spending', 'Financial Outcome'], attributes: [{ name: 'Tax Savings', unit: '$', direction: 'higher-is-better' }, { name: 'Take-home Cash Flow', unit: '$', direction: 'higher-is-better' }, { name: 'Healthcare Affordability', unit: '', direction: 'higher-is-better' }], pathways: [{ id: 'A', label: 'Pathway A', title: 'Lower Contribution / Higher Cash Flow', steps: ['Annual contribution $1,500', 'Use FSA for known recurring costs', 'Pay larger OOP costs from cash'], description: 'Lower payroll deferral, higher take-home cash, limited healthcare affordability.', values: { 'Tax Savings': '360', 'Take-home Cash Flow': '6500', 'Healthcare Affordability': 'Limited' } }, { id: 'B', label: 'Pathway B', title: 'Balanced Contribution / Balanced Wellness', steps: ['Annual contribution $3,500', 'Cover routine OOP from HSA', 'Maintain emergency cash buffer'], description: 'Balanced payroll deferral, balanced financial wellness across cash flow and tax savings.', values: { 'Tax Savings': '840', 'Take-home Cash Flow': '4500', 'Healthcare Affordability': 'Good' } }, { id: 'C', label: 'Pathway C', title: 'Maximum Contribution / Highest Tax Savings', steps: ['Annual contribution $4,150 (IRS max)', 'Pay OOP from HSA debit', 'Invest excess HSA balance long-term'], description: 'Maximum payroll deferral, highest tax savings, strongest long-term outcome.', values: { 'Tax Savings': '996', 'Take-home Cash Flow': '3850', 'Healthcare Affordability': 'Excellent' } }], evaluationCriteria: ['Quantifies the tax-savings vs cash-flow tradeoff with specific dollar figures', 'Addresses healthcare affordability and out-of-pocket coverage', 'Considers long-term compounding and retirement-style implications', 'References the member\'s liquidity / emergency reserves', 'Acknowledges IRS contribution limits and plan eligibility'] } },
                                              { label: 'Retirement Contribution Strategy', data: { showJustification: false, enableAIScoring: false, title: 'Retirement Contribution Strategy — Account Mix Optimization', description: 'Choose how to allocate retirement savings across taxable, tax-deferred, and tax-advantaged accounts.', workflow: ['Income Review', 'Goal Setting', 'Contribution Allocation', 'Account Selection', 'Investment Horizon', 'Retirement Outcome'], attributes: [{ name: 'Annual Tax Savings', unit: '$', direction: 'higher-is-better' }, { name: 'Projected Retirement Income', unit: '$/mo', direction: 'higher-is-better' }, { name: 'Liquidity', unit: '', direction: 'higher-is-better' }], pathways: [{ id: 'A', label: 'Pathway A', title: 'Taxable Brokerage Only', steps: ['Open taxable brokerage', 'Auto-invest monthly', 'Rebalance annually'], description: 'Full liquidity, no tax shelter, simplest account structure.', values: { 'Annual Tax Savings': '0', 'Projected Retirement Income': '1800', 'Liquidity': 'High' } }, { id: 'B', label: 'Pathway B', title: '401(k) Match + IRA', steps: ['Contribute to 401(k) up to match', 'Max IRA ($7,000)', 'Auto-invest both accounts'], description: 'Captures employer match plus IRA tax shelter; balanced liquidity.', values: { 'Annual Tax Savings': '2400', 'Projected Retirement Income': '3200', 'Liquidity': 'Moderate' } }, { id: 'C', label: 'Pathway C', title: 'Max 401(k) + IRA + HSA Stack', steps: ['Max 401(k) ($23,000)', 'Max IRA ($7,000)', 'Max HSA ($4,150)', 'Invest HSA long-term'], description: 'Maximum tax shelter across all three account types; lowest liquidity.', values: { 'Annual Tax Savings': '8500', 'Projected Retirement Income': '5400', 'Liquidity': 'Low' } }] } },
                                            ] },
                                          ].map((group) => (
                                            <div key={group.category} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                              <span style={{ fontSize: '11px', color: 'var(--text-faint)', alignSelf: 'center', minWidth: 150, fontWeight: 600 }}>{group.category}:</span>
                                              {group.samples.map((sample) => (
                                                <button
                                                  key={sample.label}
                                                  type="button"
                                                  onClick={() => {
                                                    updateLocalResource(index, {
                                                      content: JSON.stringify(sample.data, null, 2),
                                                      name: sample.data.title,
                                                    });
                                                  }}
                                                  style={{
                                                    padding: '4px 12px',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    background: 'var(--status-info-bg)',
                                                    color: 'var(--brand-primary)',
                                                    border: '1px solid #bfdbfe',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                  }}
                                                >
                                                  {sample.label}
                                                </button>
                                              ))}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ) :

                                  /* URL / Upload field — type-aware */
                                  (resource.studyResourceTypeId === 1 || resource.studyResourceTypeId === 13) && !isViewMode ? (
                                    <div className="lmb-resource-modal-field">
                                      <label className="lmb-muted lmb-label">File / URL</label>
                                      {/* Tab switcher */}
                                      <div style={{ display: 'flex', gap: '0', marginBottom: '8px', borderBottom: '2px solid var(--border-default)' }}>
                                        {([['url', 'Provide URL'], ['upload', 'Upload File'], ['gdrive', 'Google Drive']] as const).map(([tab, label]) => (
                                          <button
                                            key={tab}
                                            type="button"
                                            onClick={() => setUploadTab(tab)}
                                            style={{
                                              padding: '6px 16px',
                                              fontSize: '13px',
                                              fontWeight: uploadTab === tab ? 600 : 400,
                                              color: uploadTab === tab ? '#2563eb' : '#6b7280',
                                              background: 'none',
                                              border: 'none',
                                              borderBottom: uploadTab === tab ? '2px solid #2563eb' : '2px solid transparent',
                                              marginBottom: '-2px',
                                              cursor: 'pointer',
                                            }}
                                          >
                                            {label}
                                          </button>
                                        ))}
                                      </div>

                                      {uploadTab === 'url' ? (
                                        <div>
                                          <input
                                            className="lmb-input lmb-input-sm"
                                            value={resource.url || ""}
                                            onChange={(e) => updateLocalResource(index, { url: e.target.value })}
                                            placeholder="https://example.com/document.pdf"
                                            disabled={uploadingIndex === index}
                                          />
                                          {resource.url && (
                                            <div style={{ marginTop: '6px', padding: '8px 10px', background: 'var(--status-success-bg)', borderRadius: '6px', fontSize: '13px', color: 'var(--status-success-text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                              <span style={{ wordBreak: 'break-all' }}>
                                                {StudyResourceService.extractFilename(resource.url)}
                                              </span>
                                              <button
                                                type="button"
                                                onClick={() => window.open(resource.url, '_blank')}
                                                style={{ background: 'none', border: 'none', color: 'var(--brand-primary)', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap', marginLeft: '8px' }}
                                              >
                                                Open
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      ) : uploadTab === 'gdrive' ? (
                                        <div>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              openGDrivePicker(async (result) => {
                                                setGdriveUploading(true);
                                                try {
                                                  const response = await StudyResourceService.uploadFromGoogleDrive(
                                                    result.fileId, result.fileName, result.accessToken
                                                  );
                                                  if (response.success) {
                                                    updateLocalResource(index, { url: response.data.url });
                                                    setUploadTab("url");
                                                  }
                                                } catch (err: any) {
                                                  console.error("Google Drive upload failed:", err);
                                                  alert(err?.response?.data?.message || "Failed to upload from Google Drive");
                                                } finally {
                                                  setGdriveUploading(false);
                                                }
                                              });
                                            }}
                                            disabled={gdrivePickerLoading || gdriveUploading}
                                            style={{
                                              width: '100%',
                                              padding: '10px 16px',
                                              fontSize: '13px',
                                              fontWeight: 500,
                                              color: 'var(--text-on-brand)',
                                              background: gdrivePickerLoading || gdriveUploading ? '#93c5fd' : '#2563eb',
                                              border: 'none',
                                              borderRadius: '6px',
                                              cursor: gdrivePickerLoading || gdriveUploading ? 'not-allowed' : 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              gap: '8px',
                                            }}
                                          >
                                            <svg width="18" height="18" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA"/>
                                              <path d="M43.65 25L29.9 1.2C28.55 2 27.4 3.1 26.6 4.5L3.55 44.4c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47"/>
                                              <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.85l6.15 11.65 7.55 12.15z" fill="#EA4335"/>
                                              <path d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.85 0H34.45c-1.65 0-3.2.45-4.55 1.2L43.65 25z" fill="#00832D"/>
                                              <path d="M59.85 49H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.55 1.2h50.4c1.65 0 3.2-.45 4.55-1.2L59.85 49z" fill="#2684FC"/>
                                              <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 24h27.45c0-1.55-.4-3.1-1.2-4.5l-12.7-18z" fill="#FFBA00"/>
                                            </svg>
                                            {gdriveUploading ? 'Uploading to S3...' : gdrivePickerLoading ? 'Loading...' : 'Pick from Google Drive'}
                                          </button>
                                          {gdriveUploading && (
                                            <div style={{ marginTop: '8px' }}>
                                              <ProgressBar animated now={100} label="Transferring..." />
                                              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Downloading from Drive and uploading to storage...</div>
                                            </div>
                                          )}
                                          <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '6px' }}>
                                            Select a PDF from your Google Drive. The file will be copied to our storage.
                                          </div>
                                          {resource.url && !gdriveUploading && (
                                            <div style={{ marginTop: '6px', padding: '8px 10px', background: 'var(--status-success-bg)', borderRadius: '6px', fontSize: '13px', color: 'var(--status-success-text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                              <span style={{ wordBreak: 'break-all' }}>
                                                {StudyResourceService.extractFilename(resource.url)}
                                              </span>
                                              <button
                                                type="button"
                                                onClick={() => window.open(resource.url, '_blank')}
                                                style={{ background: 'none', border: 'none', color: 'var(--brand-primary)', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap', marginLeft: '8px' }}
                                              >
                                                Open
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div>
                                          <input
                                            type="file"
                                            className="lmb-input lmb-input-sm"
                                            onChange={handleFileSelect}
                                            disabled={uploadingIndex === index}
                                            style={{ padding: '4px' }}
                                          />
                                          {selectedFile && (
                                            <div style={{ marginTop: '6px', padding: '6px 10px', background: 'var(--status-info-bg)', borderRadius: '6px', fontSize: '13px', color: 'var(--brand-primary-active)' }}>
                                              {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                                            </div>
                                          )}
                                          {uploadingIndex === index && (
                                            <div style={{ marginTop: '8px' }}>
                                              <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated />
                                              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Uploading... please wait.</div>
                                            </div>
                                          )}
                                          {resource.url && uploadingIndex !== index && (
                                            <div style={{ marginTop: '6px', padding: '8px 10px', background: 'var(--status-success-bg)', borderRadius: '6px', fontSize: '13px', color: 'var(--status-success-text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                              <span style={{ wordBreak: 'break-all' }}>
                                                {StudyResourceService.extractFilename(resource.url)}
                                              </span>
                                              <button
                                                type="button"
                                                onClick={() => window.open(resource.url, '_blank')}
                                                style={{ background: 'none', border: 'none', color: 'var(--brand-primary)', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap', marginLeft: '8px' }}
                                              >
                                                Open
                                              </button>
                                            </div>
                                          )}
                                          <button
                                            type="button"
                                            onClick={() => handleFileUpload(index)}
                                            disabled={!selectedFile || uploadingIndex === index}
                                            style={{
                                              marginTop: '8px',
                                              width: '100%',
                                              padding: '7px 12px',
                                              fontSize: '13px',
                                              fontWeight: 500,
                                              color: 'var(--text-on-brand)',
                                              background: !selectedFile || uploadingIndex === index ? '#93c5fd' : '#2563eb',
                                              border: 'none',
                                              borderRadius: '6px',
                                              cursor: !selectedFile || uploadingIndex === index ? 'not-allowed' : 'pointer',
                                            }}
                                          >
                                            {uploadingIndex === index ? 'Uploading...' : 'Upload File'}
                                          </button>
                                          <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '4px' }}>
                                            PDF, DOC, ZIP, etc. (max 100 MB)
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <>
                                      <div className="lmb-resource-modal-field">
                                        <label className="lmb-muted lmb-label">URL</label>
                                        <input
                                          className="lmb-input lmb-input-sm"
                                          value={resource.url || ""}
                                          onChange={(e) =>
                                            updateLocalResource(index, { url: e.target.value })
                                          }
                                          placeholder="https://..."
                                          disabled={isViewMode}
                                        />
                                      </div>

                                      <div className="lmb-resource-modal-field">
                                        <label className="lmb-muted lmb-label">File URL (Optional)</label>
                                        <input
                                          className="lmb-input lmb-input-sm"
                                          value={resource.fileUrl || ""}
                                          onChange={(e) =>
                                            updateLocalResource(index, { fileUrl: e.target.value })
                                          }
                                          placeholder="Path or URL to file..."
                                          disabled={isViewMode}
                                        />
                                      </div>
                                    </>
                                  )}

                                  <div className="lmb-resource-modal-field">
                                    <label className="lmb-muted lmb-label">
                                      Content {resource.studyResourceTypeId === 6 ? <span style={{ color: 'var(--status-danger)' }}>*</span> : '(Optional)'}
                                    </label>
                                    <textarea
                                      className="lmb-textarea"
                                      value={resource.content || ""}
                                      onChange={(e) =>
                                        updateLocalResource(index, { content: e.target.value })
                                      }
                                      placeholder={resource.studyResourceTypeId === 6 ? "Enter article content here (supports HTML)..." : "Additional content or notes..."}
                                      rows={3}
                                      disabled={isViewMode}
                                    />
                                  </div>
                                </>
                              )}

                              {/* Test Fields */}
                              {resource.resourceType === "test" && (
                                <>
                                  <div className="lmb-resource-modal-field">
                                    <label className="lmb-muted lmb-label">Test ID</label>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                      <input
                                        className="lmb-input lmb-input-sm"
                                        value={resource.testId || ""}
                                        onChange={(e) =>
                                          updateLocalResource(index, { testId: e.target.value })
                                        }
                                        placeholder="Enter test ID..."
                                        disabled={isViewMode}
                                        style={{ flex: 1 }}
                                      />
                                      {!isViewMode && (
                                        <button
                                          className="lmb-btn lmb-btn-primary"
                                          onClick={() => setQuizModalIndex(index)}
                                          type="button"
                                          style={{ whiteSpace: "nowrap", fontSize: "13px" }}
                                        >
                                          Create Test
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  <div className="lmb-resource-modal-field">
                                    <label className="lmb-muted lmb-label">Test Name (Optional)</label>
                                    <input
                                      className="lmb-input lmb-input-sm"
                                      value={resource.testName || ""}
                                      onChange={(e) =>
                                        updateLocalResource(index, { testName: e.target.value })
                                      }
                                      placeholder="Enter test name for display..."
                                      disabled={isViewMode}
                                    />
                                  </div>
                                </>
                              )}

                              {/* Tag Selector */}
                              {!isViewMode && availableTags && availableTags.length > 0 && (
                                <div className="lmb-resource-modal-field">
                                  <label className="lmb-muted lmb-label">Tags (Flip-Class Phases)</label>
                                  <TagMultiSelect
                                    availableTags={availableTags}
                                    selectedTags={resource.tags || []}
                                    onChange={(newTags) =>
                                      updateLocalResource(index, { tags: newTags })
                                    }
                                    placeholder="Select phases..."
                                  />
                                </div>
                              )}

                              {/* Read-only tag display in view mode */}
                              {isViewMode && resource.tags && resource.tags.length > 0 && (
                                <div className="lmb-resource-modal-field">
                                  <label className="lmb-muted lmb-label">Tags</label>
                                  <div className="lmb-resource-modal-tags">
                                    {resource.tags.map((tag) => (
                                      <span key={tag.id} className="lmb-resource-tag">
                                        {tag.displayLabel || tag.name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Action Buttons */}
                      <div className="lmb-resource-modal-actions">
                        <button
                          className="lmb-resource-modal-toggle"
                          onClick={() => toggleResourceExpand(index)}
                          type="button"
                          title={isExpanded ? "Collapse" : "Expand to edit"}
                        >
                          {isExpanded ? "▲" : "▼"}
                        </button>
                        {!isViewMode && onRemoveResource && (
                          <button
                            className="lmb-resource-modal-remove"
                            onClick={() => onRemoveResource(index)}
                            type="button"
                            title="Remove resource"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="lmb-modal-footer">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1 }}>
            {!isViewMode && (
              <>
                <button className="lmb-btn lmb-btn-primary" onClick={handleAddNewResource} type="button">
                  + Add Resource
                </button>
                <button
                  className="lmb-btn lmb-btn-secondary"
                  onClick={() => setShowResourcePool(true)}
                  type="button"
                  style={{ backgroundColor: '#6c757d', color: 'white' }}
                >
                  📚 Browse Library
                </button>
              </>
            )}
            {hasChanges && (
              <span style={{ fontSize: '13px', color: 'var(--status-warning)', marginLeft: '8px' }}>
                ● Unsaved changes
              </span>
            )}
          </div>
          {!isViewMode ? (
            <>
              <button className="lmb-btn" onClick={handleCancel} type="button">
                Cancel
              </button>
              <button
                className="lmb-btn lmb-btn-primary"
                onClick={handleSave}
                type="button"
                disabled={!hasChanges}
              >
                Save Changes
              </button>
            </>
          ) : (
            <button className="lmb-btn" onClick={onClose} type="button">
              Close
            </button>
          )}
        </div>
      </div>

      {/* Resource Pool Browser Modal */}
      <ResourcePoolBrowser
        show={showResourcePool}
        onHide={() => setShowResourcePool(false)}
        onSelectResource={handleSelectResourceFromPool}
        multiSelect={false}
      />

      {/* Quiz Creation Modal */}
      <Modal
        show={quizModalIndex !== null}
        onHide={() => setQuizModalIndex(null)}
        fullscreen
        dialogClassName="lmb-quiz-creation-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Test</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowY: "auto" }}>
          {quizModalIndex !== null && (
            <QuizFormPanel
              onCreated={(result) => {
                // Auto-populate the test ID and name on the resource
                if (quizModalIndex !== null) {
                  updateLocalResource(quizModalIndex, {
                    testId: String(result.testId),
                    testName: result.name,
                  });
                }
                setQuizModalIndex(null);
              }}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Unsaved-changes confirmation modal */}
      <Modal show={showConfirmClose} centered onHide={() => setShowConfirmClose(false)}>
        <Modal.Body className="text-center py-4">
          <p style={{ fontSize: 15, marginBottom: 16, fontWeight: 500, color: 'var(--text-strong)' }}>
            You have unsaved changes. Are you sure you want to close without saving?
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowConfirmClose(false)}
              style={{
                minWidth: '120px',
                backgroundColor: '#6b7280',
                borderColor: '#6b7280',
                color: 'var(--text-on-brand)',
                fontWeight: 500
              }}
            >
              Go Back
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setShowConfirmClose(false);
                setHasChanges(false);
                onClose();
              }}
              style={{
                minWidth: '120px',
                backgroundColor: 'var(--status-danger)',
                borderColor: 'var(--status-danger)',
                color: 'var(--text-on-brand)',
                fontWeight: 500
              }}
            >
              Yes, Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
